import type { GameId } from '@urbanrats/contracts';
import type { ClientRoot } from './gateway/index.ts';
import { useI18n } from './i18n/index.ts';

const GAME: GameId = 'traffic-pulse';

/**
 * Home of Chaos Lane, the one game we offer: what it is, how a round works, and the way in. The street scene
 * is an illustration (it never shows counts or results); real rounds are counted on live cameras in the game.
 */
export function Lobby({ root, onPlay }: { root: ClientRoot; onPlay: (id: GameId) => void }) {
  const { t } = useI18n();
  const id = root.games.some((g) => g.gameId === GAME) ? GAME : (root.games[0]?.gameId ?? GAME);
  return (
    <main className="home" data-testid="lobby">
      <section className="home-hero">
        <div className="hero-scene" aria-hidden="true">
          <div className="hero-road">
            <span className="hr-lane a" />
            <span className="hr-lane b" />
            <span className="hr-lane c" />
            <span className="hr-car k1" />
            <span className="hr-car k2" />
            <span className="hr-car k3" />
            <span className="hr-car k4" />
            <span className="hr-line" />
          </div>
          <span className="hr-plus p1">+1</span>
          <span className="hr-plus p2">+1</span>
          <span className="hr-glow" />
        </div>
        <div className="hero-copy">
          <span className="hero-live">
            <i /> {t('home.live')}
          </span>
          <h1 className="hero-title">
            Chaos <em>Lane</em>
          </h1>
          <p className="hero-tag">{t('home.tagline')}</p>
          <div className="hero-cta" data-testid={`game-${id}`}>
            <button type="button" className="confirm hero-play" onClick={() => onPlay(id)}>
              {t('home.play')} →
            </button>
            <span className="hero-chips">
              <span>{t('home.chip.round')}</span>
              <span>{t('home.chip.cameras')}</span>
              <span>{t(root.moneyMode === 'REAL' ? 'home.chip.real' : 'home.chip.demo')}</span>
            </span>
          </div>
        </div>
      </section>

      <section className="home-steps" aria-label={t('home.how')}>
        <h2>{t('home.how')}</h2>
        <ol>
          <li>
            <span className="step-n">1</span>
            <Board />
            <strong>{t('home.s1.title')}</strong>
            <p>{t('home.s1.text')}</p>
          </li>
          <li>
            <span className="step-n">2</span>
            <span className="step-art cam" aria-hidden="true">
              <span className="sa-line" />
              <span className="sa-car" />
              <span className="sa-plus">+1</span>
            </span>
            <strong>{t('home.s2.title')}</strong>
            <p>{t('home.s2.text')}</p>
          </li>
          <li>
            <span className="step-n">3</span>
            <span className="step-art result" aria-hidden="true">
              <b>23</b>
              <em>{t('home.s3.badge')}</em>
            </span>
            <strong>{t('home.s3.title')}</strong>
            <p>{t('home.s3.text')}</p>
          </li>
        </ol>
      </section>

      <section className="home-trust">
        <div>
          <strong>{t('home.t1.title')}</strong>
          <p>{t('home.t1.text')}</p>
        </div>
        <div>
          <strong>{t('home.t2.title')}</strong>
          <p>{t('home.t2.text')}</p>
        </div>
        <div>
          <strong>{t('home.t3.title')}</strong>
          <p>{t('home.t3.text')}</p>
        </div>
      </section>

      <footer className="home-foot">
        <span>{t(root.moneyMode === 'REAL' ? 'home.foot.real' : 'home.foot')}</span>
        <button type="button" className="home-foot-play" onClick={() => onPlay(id)}>
          {t('home.play')} →
        </button>
      </footer>
    </main>
  );
}

/** Miniature of the bet board (illustration: example bands, no odds). */
function Board() {
  const { t } = useI18n();
  return (
    <span className="step-art board" aria-hidden="true">
      <span>
        <em>{t('board.UNDER')}</em>≤ 18
      </span>
      <span className="x">
        <em>{t('board.EXACT_LOW')}</em>19
      </span>
      <span>
        <em>{t('board.RANGE')}</em>20–22
      </span>
      <span className="x">
        <em>{t('board.EXACT_HIGH')}</em>23
      </span>
      <span>
        <em>{t('board.OVER')}</em>≥ 24
      </span>
    </span>
  );
}
